import React, { useState, useRef, useEffect } from 'react';
import * as Three from 'three';
import useFogHook from '../../Hooks/useFog';
import {
  ColorInput,
  GridDropdown,
  GridTextInput,
  GridTwoInput,
  LabelHover,
  ToggleCheckbox,
} from '../../utils/gridUtils';
import { useDispatch, useSelector } from 'react-redux';
import { setEnvironmentData } from '../../redux/Slice/environmentSlice';
import toast from 'react-hot-toast';
import { iconsPath } from '../../utils/imagePath';
import {
  applyHdriLightIntensity,
  applyHdriToSceneVisual,
  disposeFlatHdriBackgroundTexture,
  getHdriOffFallbackBackground,
  isHdriSkyBackground,
  removeHdriBackgroundSphere,
  resetPreHdriBackgroundCapture,
  updateHdriRotation,
} from '../../utils/ThreeJsUtils/hdriSceneBackground.js';

const EnvironmentComponent = ({
  onHdriSelected,
  onChange,
  scene,
  environmentData,
  camera = null,
  historyUiRevision = 0,
}) => {
  const ENV_COLOR_DEFAULTS = {
    singleColor: '#2d2e32',
    gradientTopColor: '#6C6C6C',
    gradientBottomColor: '#000000',
  };

  /** Canvas/CSS colors reject ''; treat blank as missing. */
  const resolveCssColor = (value, fallback) => {
    if (typeof value !== 'string') return fallback;
    const trimmed = value.trim();
    return trimmed ? trimmed : fallback;
  };

  const [localData, setLocalData] = useState(() => {
    const defaults = {
      skybox: { name: 'Color', url: null },
      colorType: 'Single',
      singleColor: ENV_COLOR_DEFAULTS.singleColor,
      gradientTopColor: ENV_COLOR_DEFAULTS.gradientTopColor,
      gradientBottomColor: ENV_COLOR_DEFAULTS.gradientBottomColor,
      gradientAngle: 0,
      hdriFile: null,
      fog_checked: false,
      fogType: 'Linear',
      fogColor: '#ffffff',
      fogStart: 1,
      fogEnd: 100,
      fog_density: 0.01,
      hdriRotation: 0,
      hdriVisible: true,
      hdriLightIntensity: 1,
    };

    if (!environmentData) {
      // If no environmentData, try to extract fog color from the scene's current fog
      let fogColor = defaults.fogColor;
      if (scene && scene.fog) {
        const sceneColor = scene.fog.color;
        if (sceneColor) {
          // Convert Three.js color to hex
          fogColor = '#' + sceneColor.getHexString();
        }
      }
      return { ...defaults, fogColor };
    }

    // Get fog color from environmentData or try to extract from scene fog
    let fogColor = environmentData.fogColor || defaults.fogColor;
    if (!environmentData.fogColor && scene && scene.fog) {
      const sceneColor = scene.fog.color;
      if (sceneColor) {
        fogColor = '#' + sceneColor.getHexString();
      }
    }

    const result = {
      skybox: environmentData.skybox || defaults.skybox,
      colorType: environmentData.color_config?.color_type || defaults.colorType,
      singleColor: resolveCssColor(
        environmentData.color_config?.color,
        defaults.singleColor
      ),
      gradientTopColor: resolveCssColor(
        environmentData.color_config?.top_color,
        defaults.gradientTopColor
      ),
      gradientBottomColor: resolveCssColor(
        environmentData.color_config?.bottom_color,
        defaults.gradientBottomColor
      ),
      gradientAngle: environmentData.gradientAngle || defaults.gradientAngle,
      hdriFile: environmentData.hdriFile || defaults.hdriFile,
      fog_checked: environmentData.fog_checked || defaults.fog_checked,
      fogType: environmentData.fogType || defaults.fogType,
      fogColor: fogColor,
      fogStart: environmentData.fogStart || defaults.fogStart,
      fogEnd: environmentData.fogEnd || defaults.fogEnd,
      fog_density: environmentData.fog_density || defaults.fog_density,
      hdriRotation:
        Number(environmentData.hdriRotation) || defaults.hdriRotation,
      hdriVisible:
        environmentData.hdriVisible !== undefined
          ? environmentData.hdriVisible
          : defaults.hdriVisible,
      hdriLightIntensity:
        environmentData.hdriLightIntensity !== undefined
          ? Number(environmentData.hdriLightIntensity)
          : defaults.hdriLightIntensity,
    };

    return result;
  });

  const currentHdriRef = useRef(null);
  const cameraType = useSelector(
    (state) => state?.assets_object?.cameraType ?? 'perspective'
  );
  const fileInputRef = useRef(null);
  const originalBackgroundRef = useRef(null);
  const dispatch = useDispatch();
  const [showMaxWarning, setShowMaxWarning] = useState(false);
  const [showMinWarning, setShowMinWarning] = useState(false);

  // HDRI caching system
  const hdriCacheRef = useRef(new Map());
  const lastHdriConfigRef = useRef(null);

  // Sync local state with environmentData when it changes (including undo/redo).
  useEffect(() => {
    if (!environmentData) return;

    let fogColor = environmentData.fogColor;
    if (!fogColor && scene?.fog?.color) {
      fogColor = '#' + scene.fog.color.getHexString();
    }

    setLocalData((prev) => ({
      skybox: environmentData.skybox ?? prev.skybox,
      colorType:
        environmentData.color_config?.color_type ?? prev.colorType,
      singleColor: resolveCssColor(
        environmentData.color_config?.color,
        prev.singleColor || ENV_COLOR_DEFAULTS.singleColor
      ),
      gradientTopColor: resolveCssColor(
        environmentData.color_config?.top_color,
        prev.gradientTopColor || ENV_COLOR_DEFAULTS.gradientTopColor
      ),
      gradientBottomColor: resolveCssColor(
        environmentData.color_config?.bottom_color,
        prev.gradientBottomColor || ENV_COLOR_DEFAULTS.gradientBottomColor
      ),
      gradientAngle: environmentData.gradientAngle ?? prev.gradientAngle,
      hdriFile: environmentData.hdriFile ?? prev.hdriFile,
      fog_checked:
        environmentData.fog_checked !== undefined
          ? environmentData.fog_checked
          : prev.fog_checked,
      fogType: environmentData.fogType ?? prev.fogType,
      fogColor: fogColor ?? prev.fogColor,
      fogStart: environmentData.fogStart ?? prev.fogStart,
      fogEnd: environmentData.fogEnd ?? prev.fogEnd,
      fog_density: environmentData.fog_density ?? prev.fog_density,
      hdriRotation:
        environmentData.hdriRotation !== undefined
          ? Number(environmentData.hdriRotation)
          : prev.hdriRotation,
      hdriVisible:
        environmentData.hdriVisible !== undefined
          ? environmentData.hdriVisible
          : prev.hdriVisible,
      hdriLightIntensity:
        environmentData.hdriLightIntensity !== undefined
          ? Number(environmentData.hdriLightIntensity)
          : prev.hdriLightIntensity,
    }));
  }, [environmentData, scene, historyUiRevision]);

  useEffect(() => {
    if (!scene) return;

    if (localData.fog_checked) {
      const fogColor = localData.fogColor ? parseInt(localData.fogColor.replace('#', ''), 16) : 0xffffff;
      scene.fog =
        localData.fogType === 'Linear'
          ? new Three.Fog(
            fogColor,
            Number(localData.fogStart),
            Number(localData.fogEnd)
          )
          : new Three.FogExp2(fogColor, Number(localData.fog_density));
    } else {
      scene.fog = null;
    }
  }, [
    scene,
    localData.fog_checked,
    localData.fogType,
    localData.fogColor,
    localData.fogStart,
    localData.fogEnd,
    localData.fog_density,
  ]);

  const updateReduxAndCallback = (updatedData) => {
    const {
      colorType,
      singleColor,
      gradientTopColor,
      gradientBottomColor,
      ...rest
    } = updatedData;

    const environmentPayload = {
      ...rest,
      hdriFile: rest.hdriFile || '',
      hdriRotation: String(rest.hdriRotation || 0),
      hdriLightIntensity: Number(rest.hdriLightIntensity ?? 1),
      color_config: {
        color_type: colorType,
        color: colorType === 'Single' ? singleColor : '',
        top_color: colorType === 'Gradient' ? gradientTopColor : '',
        bottom_color: colorType === 'Gradient' ? gradientBottomColor : '',
      },
    };

    dispatch(setEnvironmentData(environmentPayload));

    if (onChange) onChange(updatedData);
  };

  const handleChange = (value, field) => {
    setLocalData((prev) => {
      let updatedData;

      if (field === 'skybox') {
        updatedData = {
          ...prev,
          skybox: { name: value, url: null },
        };
        if (value !== 'HDRI' && scene) {
          resetPreHdriBackgroundCapture(scene);
        }
      } else {
        updatedData = {
          ...prev,
          [field]: value,
        };
      }

      // Update Redux
      updateReduxAndCallback(updatedData);

      return updatedData;
    });
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleHdriFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    const validExtensions = ['.hdr'];
    const fileExtension = file.name
      .slice(file.name.lastIndexOf('.'))
      .toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      toast.error('Unsupported file type! Please upload .hdr file only.', {
        style: {
          background: '#121316', // black background
          color: '#fff', // white text
          fontSize: '16px',
        },
      });
      e.target.value = ''; // reset input
      return;
    }

    try {
      const base64 = await fileToBase64(file);

      const updatedData = {
        ...localData,
        hdriFile: base64,
        skybox: {
          name: 'HDRI',
          url: base64,
        },
        hdriVisible: true,
      };

      setLocalData(updatedData);
      updateReduxAndCallback(updatedData);

      const rotation = Number(localData.hdriRotation || 0);

      if (onHdriSelected && file) {
        onHdriSelected(
          file,
          null,
          rotation,
          scene,
          (loadedTexture) => {
            currentHdriRef.current = loadedTexture;
          },
          updatedData.hdriVisible,
          camera,
          Number(updatedData.hdriLightIntensity ?? 1)
        );
      }
    } catch (error) {
      console.error('Error processing HDRI file:', error);
    }
  };

  const handleFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    } else {
      console.error('File input ref is null');
    }
  };

  const createGradientTexture = (topColor, bottomColor) => {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const context = canvas.getContext('2d');

    const gradient = context.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(
      0,
      resolveCssColor(topColor, ENV_COLOR_DEFAULTS.gradientTopColor)
    );
    gradient.addColorStop(
      1,
      resolveCssColor(bottomColor, ENV_COLOR_DEFAULTS.gradientBottomColor)
    );

    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width, canvas.height);

    return new Three.CanvasTexture(canvas);
  };

  // HDRI caching function
  const getCachedHdriTexture = (hdriFile) => {
    if (hdriFile && hdriCacheRef.current.has(hdriFile)) {
      return hdriCacheRef.current.get(hdriFile);
    }
    return null;
  };

  const setCachedHdriTexture = (hdriFile, texture) => {
    if (hdriFile && texture) {
      hdriCacheRef.current.set(hdriFile, texture);
    }
  };

  // Check if HDRI configuration has actually changed
  const hasHdriConfigChanged = (currentConfig) => {
    const lastConfig = lastHdriConfigRef.current;

    if (!lastConfig) return true;

    return (
      lastConfig.hdriFile !== currentConfig.hdriFile ||
      lastConfig.rotation !== currentConfig.rotation ||
      lastConfig.hdriVisible !== currentConfig.hdriVisible ||
      lastConfig.skyboxName !== currentConfig.skyboxName
    );
  };

  const updateBackground = () => {
    if (!scene) return;

    const skyboxType = localData.skybox?.name;
    const rotation = Number(localData.hdriRotation || 0);
    const hdriVisible = localData.hdriVisible !== false;
    const lightIntensity = Number(localData.hdriLightIntensity ?? 1);

    if (!currentHdriRef.current && scene.environment?.isTexture) {
      currentHdriRef.current = scene.environment;
      if (localData.hdriFile) {
        setCachedHdriTexture(localData.hdriFile, currentHdriRef.current);
      }
    }

    const currentConfig = {
      hdriFile: localData.hdriFile,
      rotation,
      hdriVisible,
      skyboxName: skyboxType,
    };
    const configChanged = hasHdriConfigChanged(currentConfig);

    const envMapRef =
      currentHdriRef.current ||
      getCachedHdriTexture(localData.hdriFile) ||
      scene.userData?.__hdriStoredEnvMap ||
      (scene.environment?.isTexture ? scene.environment : null);

    if (
      originalBackgroundRef.current === null &&
      scene.background &&
      !scene.getObjectByName('hdriBackgroundSphere') &&
      !isHdriSkyBackground(scene.background, envMapRef, scene)
    ) {
      originalBackgroundRef.current = scene.background;
    }

    if (skyboxType === 'HDRI' && localData.hdriFile) {
      if (!hdriVisible) {
        if (envMapRef?.isTexture) {
          currentHdriRef.current = envMapRef;
        }
        applyHdriToSceneVisual(
          scene,
          envMapRef,
          rotation,
          false,
          camera,
          getHdriOffFallbackBackground(
            scene,
            envMapRef,
            originalBackgroundRef.current
          ),
          lightIntensity
        );
      } else if (envMapRef?.isTexture) {
        currentHdriRef.current = envMapRef;
        setCachedHdriTexture(localData.hdriFile, envMapRef);

        const useSphere =
          camera?.isOrthographicCamera === true;
        const sphere = scene.getObjectByName('hdriBackgroundSphere');
        const needsFullApply =
          useSphere !== !!sphere ||
          (useSphere && sphere?.material?.map !== envMapRef) ||
          (!useSphere &&
            (scene.background !== envMapRef ||
              !isHdriSkyBackground(scene.background, envMapRef, scene)));

        if (!needsFullApply) {
          scene.environment = envMapRef;
          updateHdriRotation(scene, rotation, camera);
          applyHdriLightIntensity(scene, lightIntensity);
        } else {
          applyHdriToSceneVisual(
            scene,
            envMapRef,
            rotation,
            true,
            camera,
            null,
            lightIntensity
          );
        }
      }
    } else if (skyboxType === 'Color') {
      resetPreHdriBackgroundCapture(scene);
      removeHdriBackgroundSphere(scene);
      disposeFlatHdriBackgroundTexture(scene);
      if (localData.colorType === 'Gradient') {
        scene.background = createGradientTexture(
          localData.gradientTopColor,
          localData.gradientBottomColor
        );
      } else {
        scene.background = new Three.Color(
          resolveCssColor(
            localData.singleColor,
            ENV_COLOR_DEFAULTS.singleColor
          )
        );
      }
      scene.environment = null;
      if (scene.environmentRotation) scene.environmentRotation.y = 0;
    } else {
      resetPreHdriBackgroundCapture(scene);
      removeHdriBackgroundSphere(scene);
      disposeFlatHdriBackgroundTexture(scene);
      scene.background =
        originalBackgroundRef.current || new Three.Color(0x2d2e32);
      scene.environment = null;
      if (scene.environmentRotation) scene.environmentRotation.y = 0;
    }

    lastHdriConfigRef.current = currentConfig;
  };

  useEffect(updateBackground, [
    localData.skybox,
    localData.colorType,
    localData.singleColor,
    localData.gradientTopColor,
    localData.gradientBottomColor,
    localData.hdriRotation,
    localData.hdriVisible,
    localData.hdriLightIntensity,
    localData.hdriFile,
    camera,
    cameraType,
  ]);

  // Handle HDRI file loading from URL when coming from backend
  useEffect(() => {
    if (
      localData.skybox?.name === 'HDRI' &&
      localData.hdriFile &&
      onHdriSelected &&
      !localData.hdriFile.startsWith('data:') // Not a base64 file
    ) {
      const rotation = Number(localData.hdriRotation || 0);

      // Create a fake file object for URL-based HDRI
      const fakeFile = {
        name: 'hdri_from_url.hdr',
        type: 'image/hdr',
        url: localData.hdriFile,
      };

      onHdriSelected(
        fakeFile,
        localData.hdriFile,
        rotation,
        scene,
        (loadedTexture) => {
          currentHdriRef.current = loadedTexture;
        },
        localData.hdriVisible,
        camera,
        Number(localData.hdriLightIntensity ?? 1)
      );
    }
  }, [
    localData.skybox?.name,
    localData.hdriFile,
    onHdriSelected,
  ]);

  // Do not clear HDRI cache on unmount — switching to Camera (or other panels)
  // remounts this component and clearing cache left rotation changes with no texture.

  return (
    <div className="text-xs flex flex-col gap-1 py-2">
      <GridDropdown
        label="Skybox"
        value={localData.skybox?.name || 'Color'}
        onChange={(v) => handleChange(v.target.value, 'skybox')}
        options={[
          { value: 'Color', label: 'Color' },
          { value: 'HDRI', label: 'HDRI' },
        ]}
      />

      <input
        type="file"
        accept=".hdr"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleHdriFileChange}
      />

      {localData.skybox?.name === 'HDRI' && (
        <div className="mb-2 flex items-center gap-2">
          <LabelHover label="File" />
          <div className="relative flex-[16] text-[10px]">
            <button
              type="button"
              onClick={handleFileSelect}
              className="w-full flex items-center justify-center gap-1 px-2 py-1 bg-[#2B2C2F] text-[#9CA3AF] rounded transition-colors hover:bg-[#3F3F3F]"
            >
              <img
                className="h-4 w-4"
                src={iconsPath.uploadIcon}
                alt="upload icon"
              />
              <span className="text-[10px]">
                {localData.hdriFile ? 'Change File' : 'Upload'}
              </span>
            </button>
          </div>
        </div>
      )}
      {localData.skybox?.name === 'Color' && (
        <>
          <GridDropdown
            label="Color Type"
            value={localData.colorType}
            onChange={(v) => handleChange(v.target.value, 'colorType')}
            options={[
              { value: 'Single', label: 'Single' },
              { value: 'Gradient', label: 'Gradient' },
            ]}
          />
          {localData.colorType === 'Single' && (
            <ColorInput
              label="Color"
              color={localData.singleColor}
              setColor={(value) => handleChange(value, 'singleColor')}
            />
          )}
          {localData.colorType === 'Gradient' && (
            <div className="flex flex-col gap-2">
              <ColorInput
                label="Top Color"
                color={localData.gradientTopColor}
                setColor={(value) => handleChange(value, 'gradientTopColor')}
              />
              <ColorInput
                label="Bottom Color"
                color={localData.gradientBottomColor}
                setColor={(value) => handleChange(value, 'gradientBottomColor')}
              />
            </div>
          )}
        </>
      )}

      {localData.skybox?.name === 'HDRI' && localData.hdriFile && (
        <>
          <ToggleCheckbox
            label="Enable HDRI Visibility"
            checked={localData.hdriVisible === true}
            onToggle={() => handleChange(!localData.hdriVisible, 'hdriVisible')}
          />
          <div className="mb-2 flex items-center gap-2">
            <LabelHover label="HDRI Rotation" />
            <div className="flex-[16] flex items-center justify-start pl-2 gap-2 min-w-0">
              <input
                type="range"
                min={0}
                max={360}
                step={1}
                value={Number(localData.hdriRotation || 0)}
                onChange={(e) =>
                  handleChange(Number(e.target.value), 'hdriRotation')
                }
                className="cursor-pointer flex-1 min-w-0 h-1 bg-[#2B2C2F] rounded-lg appearance-none accent-[#9ca3af]"
              />
              <div className="flex items-center gap-1 shrink-0">
                <input
                  type="number"
                  min={0}
                  max={360}
                  step={1}
                  value={localData.hdriRotation || 0}
                  onChange={(e) => {
                    const value = Math.min(
                      360,
                      Math.max(0, parseInt(e.target.value, 10) || 0)
                    );
                    handleChange(value, 'hdriRotation');
                  }}
                  className="w-8 px-1 py-0.5 bg-[#2B2C2F] border border-gray-700 rounded text-white focus:outline-none focus:border-blue-500 text-[10px]"
                />
                <span className="text-gray-500 text-[10px]">°</span>
              </div>
            </div>
          </div>
          <div className="mb-2 flex items-center gap-2">
            <LabelHover label="Light Intensity" />
            <div className="flex-[16] flex items-center justify-start pl-2 gap-2 min-w-0">
              <input
                type="range"
                min={0}
                max={5}
                step={0.05}
                value={Number(localData.hdriLightIntensity ?? 1)}
                onChange={(e) =>
                  handleChange(
                    Math.min(5, Math.max(0, parseFloat(e.target.value) || 0)),
                    'hdriLightIntensity'
                  )
                }
                className="cursor-pointer flex-1 min-w-0 h-1 bg-[#2B2C2F] rounded-lg appearance-none accent-[#9ca3af]"
              />
              <input
                type="number"
                min={0}
                max={5}
                step={0.05}
                value={localData.hdriLightIntensity ?? 1}
                onChange={(e) => {
                  const value = Math.min(
                    5,
                    Math.max(0, parseFloat(e.target.value) || 0)
                  );
                  handleChange(value, 'hdriLightIntensity');
                }}
                className="w-10 shrink-0 px-1 py-0.5 bg-[#2B2C2F] border border-gray-700 rounded text-white focus:outline-none focus:border-blue-500 text-[10px]"
              />
            </div>
          </div>
        </>
      )}

      <ToggleCheckbox
        label="Fog"
        checked={localData.fog_checked}
        onToggle={() => handleChange(!localData.fog_checked, 'fog_checked')}
      />

      {localData.fog_checked && (
        <>
          <GridDropdown
            label="Fog Type"
            value={localData.fogType}
            onChange={(v) => handleChange(v.target.value, 'fogType')}
            options={[
              { value: 'Linear', label: 'Linear' },
              { value: 'Exponential', label: 'Exponential' },
              { value: 'Exponential Squared', label: 'Exponential Squared' },
            ]}
          />
          <ColorInput
            label="Fog Color"
            color={localData.fogColor}
            setColor={(value) => handleChange(value, 'fogColor')}
          />
          {localData.fogType === 'Linear' && (
            <>
              <GridTwoInput
                label="Fog Distance"
                value1={localData.fogStart}
                value2={localData.fogEnd}
                min1={0.1}
                max1={1000}
                min2={0}
                max2={1000}
                step={0.1}
                onKeyDown1={(e) => {
                  let num = parseFloat(localData.fogStart);
                  if (e.key === 'ArrowUp' && num >= 1000) {
                    setShowMaxWarning(true);
                    setTimeout(() => setShowMaxWarning(false), 2000);
                  }
                  if (e.key === 'ArrowDown' && num <= 0.1) {
                    setShowMinWarning(true);
                    setTimeout(() => setShowMinWarning(false), 2000);
                  }
                }}
                onKeyDown2={(e) => {
                  let num = parseFloat(localData.fogEnd);
                  if (e.key === 'ArrowUp' && num >= 1000) {
                    setShowMaxWarning(true);
                    setTimeout(() => setShowMaxWarning(false), 2000);
                  }
                }}
                onChange1={(e) => {
                  let val = e.target.value;
                  if (val === '') {
                    setShowMinWarning(true);
                    setTimeout(() => setShowMinWarning(false), 2000);
                  }
                  
                  let num = parseFloat(val);
                  if (!isNaN(num) && num > 1000) {
                    setShowMaxWarning(true);
                    setTimeout(() => setShowMaxWarning(false), 2000);
                    handleChange(1000, 'fogStart');
                    return;
                  }
                  handleChange(val, 'fogStart');
                }}
                onChange2={(e) => {
                  let val = e.target.value;
                  let num = parseFloat(val);
                  if (!isNaN(num) && num > 1000) {
                    setShowMaxWarning(true);
                    setTimeout(() => setShowMaxWarning(false), 2000);
                    handleChange(1000, 'fogEnd');
                    return;
                  }
                  handleChange(val, 'fogEnd');
                }}
                onBlur1={(e) => {
                  let num = parseFloat(e.target.value);
                  if (isNaN(num) || num < 0.1) {
                    setShowMinWarning(true);
                    setTimeout(() => setShowMinWarning(false), 2000);
                    handleChange(0.1, 'fogStart');
                  } else {
                    handleChange(num, 'fogStart');
                  }
                }}
                onBlur2={(e) => {
                  let num = parseFloat(e.target.value);
                  if (isNaN(num)) num = 100;
                  handleChange(num, 'fogEnd');
                }}
                placeholder1="Start"
                placeholder2="End"
              />
              {showMaxWarning && (
                <div className="text-[#EE2B2A] text-[10px] font-semibold mb-2">
                  Max limit is 1000
                </div>
              )}
              {showMinWarning && (
                <div className="text-[#EE2B2A] text-[10px] font-semibold mb-2">
                  Min limit is 0.1
                </div>
              )}
            </>
          )}
          {(localData.fogType === 'Exponential' ||
            localData.fogType === 'Exponential Squared') && (
              <GridTextInput
                label="Fog Density"
                value={localData.fog_density}
                onChange={(v) => handleChange(v.target.value, 'fog_density')}
                placeholder="Enter density"
                className="w-full"
              />
            )}
        </>
      )}
    </div>
  );
};

export default EnvironmentComponent;
